import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { getNews, getNewsItem } from "../api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowLeft, Calendar, User, ChevronRight } from "lucide-react";

const NewsDetail = ({ newsId }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await getNewsItem(newsId);
        setArticle(data);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [newsId]);

  if (loading) {
    return (
      <div className="py-12 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Skeleton className="h-4 w-32 mb-8" />
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="aspect-video w-full rounded-2xl mb-10" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-2xl font-bold text-slate-800">Article non trouvé</h2>
        <Link to="/notre-actualite" className="text-sky-500 hover:text-sky-600 mt-4 inline-block">
          Retour aux actualités
        </Link>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/notre-actualite"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-sky-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux actualités
          </Link>
        </div>
      </section>

      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-sky-400" />
                {new Date(article.date).toLocaleDateString("fr-FR", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4 text-sky-400" />
                {article.author}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              {article.title}
            </h1>
          </div>

          {article.image && (
            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>
          )}

          <div className="prose prose-slate max-w-none">
            {article.content.split("\n\n").map((paragraph, index) => (
              <div key={index} className="mb-6">
                {paragraph.includes("\n-") ? (
                  <>
                    <p className="text-slate-600 leading-relaxed mb-3">
                      {paragraph.split("\n")[0]}
                    </p>
                    <ul className="space-y-2 ml-4">
                      {paragraph
                        .split("\n")
                        .filter((line) => line.startsWith("- "))
                        .map((item, i) => (
                          <li
                            key={i}
                            className="text-slate-600 flex items-start gap-2"
                          >
                            <ChevronRight className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                            <span>{item.replace("- ", "")}</span>
                          </li>
                        ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-slate-600 leading-relaxed">
                    {paragraph}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
};

export default function NotreActualite() {
  const { id } = useParams();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      const fetchNews = async () => {
        try {
          const data = await getNews();
          setNewsList(data);
        } catch (error) {
          console.error("Error fetching news:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchNews();
    }
  }, [id]);

  if (id) {
    return <NewsDetail newsId={id} />;
  }

  return (
    <div>
      {/* Page Header */}
      <section className="bg-gradient-to-br from-slate-50 via-sky-50/40 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 px-4 py-1.5 text-xs font-semibold mb-4">
            Restez informé
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Notre actualité
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl">
            Suivez les dernières nouvelles de notre section syndicale et les
            événements à venir.
          </p>
        </div>
      </section>

      {/* News List */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="overflow-hidden border-0 shadow-sm">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : newsList.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Aucune actualité pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newsList.map((article) => (
                <Link
                  key={article.id}
                  to={`/notre-actualite/${article.id}`}
                  className="group"
                >
                  <Card className="h-full overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 group-hover:-translate-y-1">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-sky-400" />
                          {new Date(article.date).toLocaleDateString("fr-FR", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5 text-sky-400" />
                          {article.author}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-sky-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-3">
                        {article.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
